import React, { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button }from '@cmsgov/design-system';
import styled from 'styled-components';

import useForm from '@hooks/useForm';
import { DEFAULT_SELECT, DEFAULT_EXPIRATION_DATE, TICKET_TYPE_FORM_NAMES, FIELD_TYPES, ALERT_TYPES, CATEGORY_FORM_NAMES, ENTITY_TYPES } from './ticketFormConfigs';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { createObjectWrapper } from '@utils/miscellaneous/objectHelpers';

const StyledContainer = styled.div`
    max-height: 20vh;
    overflow-x: auto;
    overflow-y: scroll;
    border: 1px solid ${({ theme }) => theme.gray7};
`;

const TicketForm = (props) => {
    const {
        entity,
        initialFormValues,
        formFields,
        onSaveHandler,
        onCancelHandler
    } = props;

    const [alertMsg, setAlertMsg] = useState('');
    const scrollRef = useRef(null);
    const [ inputValidationErrors, setInputValidationErrors ] = useState({});
    const { values, handlers, reset, setArrayValue } = useForm(initialFormValues);
    const [showSpanishTextField, setSpanishTextField] = useState(initialFormValues[CATEGORY_FORM_NAMES.SPANISH_NAME]);

    const validateValues = () => {
        const errors = {};

        formFields.map(field => {
            if(field.name === CATEGORY_FORM_NAMES.SPANISH_NAME && !showSpanishTextField){
                return;
            }
            if (field.validation && field.validation.regExp) {
                const regExp = field.validation.regExp;
                values[field.name] = values[field.name].trim();

                if (regExp && !regExp.test(values[field.name]))  {
                    // Unique error msgs can be added if needed.
                    // For now there is one general msg on the top.
                    errors[field.name] = true;
                }
            }

            if (values[field.name] === DEFAULT_SELECT) {
                errors[field.name] = true;
            }

        });

        let isValid = Object.keys(errors).length <= 0;

        setInputValidationErrors({ ...errors });

        !isValid && setAlertMsg(ALERT_TYPES.VALIDATION);

        return isValid;
    };
    
    const handleClick = (e, index, requiredField) => {
        const fieldValue = e.target.checked;
        const scrollTop = scrollRef.current.scrollTop;
        setArrayValue(FIELD_TYPES.CUSTOM_FIELD, index, fieldValue, requiredField );

        setTimeout(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollTop;
            }
        },0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setAlertMsg('');

        if ( validateValues()) {
            let errorMsg = await onSaveHandler(values);
            if (errorMsg) {
                setAlertMsg(ALERT_TYPES[errorMsg]);
            } else {
                reset();
                onCancelHandler();
            }
        }
    };

    const handleCancel = e => {
        e.preventDefault();
        setAlertMsg('');
        reset();
        onCancelHandler();
    };
    const toogleSpanishTextField = e => {
        e.preventDefault();
        setSpanishTextField(!showSpanishTextField);
        values[CATEGORY_FORM_NAMES.SPANISH_NAME] = '';
    };

    const getSpanishTextField = (fieldName, field, error) => {
        if (entity === ENTITY_TYPES.TICKET && values.permission !== 'TKM_WORKFLOW_PUBLIC1') {
            let spanishTextPresent = values[CATEGORY_FORM_NAMES.SPANISH_NAME];
            setSpanishTextField(spanishTextPresent);
            return;
        }
        if (!showSpanishTextField) {
            return (
                <a href="" onClick={e => toogleSpanishTextField(e)}>
                    + <FormattedMessage id="ticket_management.form_header.spanish_name" />
                </a>
            );
        }
        return (
            <label key={fieldName} className="ticket-form__label">
                {field.label}
                <div className="ticket-form__icon-section">
                    <input
                        type="text"
                        className={`${error ? 'ticket-form__input ticket-form__input--error' : 'ticket-form__input'}`}
                        {...handlers[fieldName]}
                        maxLength={field.validation && field.validation.maxLength}
                    />
                    <i className="icon" onClick={e => toogleSpanishTextField(e)}>
                        <FontAwesomeIcon icon="trash-alt" size="1x" />
                    </i>
                </div>
            </label>
        );
    };
   
    const constructFormFields = (formFields) => {
        

        const index = formFields.findIndex(field => field.name === TICKET_TYPE_FORM_NAMES.DUE_DATE);
        const EXPIRATION_DATE_CONSTANT_USE = 999;
        if (values.permission === 'TKM_WORKFLOW_VERIFY') {
            if (!formFields[index].options.includes(DEFAULT_EXPIRATION_DATE)) {
                formFields[index].options.push(DEFAULT_EXPIRATION_DATE);
            }
        } else {
            if (values.expDate && values.expDate.toString() === EXPIRATION_DATE_CONSTANT_USE.toString()) {
                values.expDate = DEFAULT_SELECT;
            }
            if (formFields[index] && formFields[index].options.includes(DEFAULT_EXPIRATION_DATE)) {
                formFields[index].options.pop();
            }
        }

        return formFields.map(field => {
            const fieldName = field.name;
            let error = inputValidationErrors && inputValidationErrors[fieldName];

            switch (field.type) {
                case FIELD_TYPES.TEXT_INPUT:
                    return (
                        <label key={fieldName} className="ticket-form__label">
                            {field.label}
                            <input
                                type={field.type}
                                className={`${error ? 'ticket-form__input ticket-form__input--error' : 'ticket-form__input'}`}
                                {...handlers[fieldName]}
                                maxLength={field.validation && field.validation.maxLength}
                            />
                        </label>
                    );
                case FIELD_TYPES.SPANISH_NAME:
                    return getSpanishTextField(fieldName, field, error);

                case FIELD_TYPES.CHECKBOX_INPUT:
                    return (
                        <span key={fieldName} className={'ticket-form__' + fieldName}>
                            <input
                                className="ticket-form__checkbox"
                                checked={values[fieldName]}
                                type={FIELD_TYPES.CHECKBOX_INPUT}
                                {...handlers[fieldName]}
                                id={fieldName}
                            />
                            <label htmlFor={fieldName} className="ticket-form__label ticket-form__label--checkbox">
                                {field.label}
                            </label>
                        </span>
                    );

                case FIELD_TYPES.SELECT:
                    return (
                        <label key={fieldName} className="ticket-form__label">
                            {field.label}
                            <select
                                key={fieldName}
                                className={`${error ? 'ticket-form__select ticket-form__input--error' : 'ticket-form__select'}`}
                                {...handlers[fieldName]}
                            >
                                {field.options.map((option, index) => {
                                    // option may be a simple string, in that case label and value are the same.
                                    // or it can be an object containing those properties.
                                    const { label = option } = option;
                                    let { value = option } = option;
                                    if (
                                        option === DEFAULT_EXPIRATION_DATE &&
                                        fieldName === TICKET_TYPE_FORM_NAMES.DUE_DATE
                                    ) {
                                        value = EXPIRATION_DATE_CONSTANT_USE;
                                    }
                                    return (
                                        <option key={index} value={value}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                        </label>
                    );

                case FIELD_TYPES.CUSTOM_FIELD:
                    if (initialFormValues[fieldName].length === 0) {
                        return null;
                    }
                    return (
                        <div className="ticket-form__label ticket-form__customFields">
                            <fieldset>
                                <label> <FormattedMessage id='ticket_management.form_header.custom_field'/> </label>
                                <div className='float-right'> <FormattedMessage id='ticket_management.form_header.custom_field.required'/> </div>
                                <StyledContainer ref={scrollRef}>
                                    <Table bordered className="table table-striped">
                                        <tbody>
                                            {createObjectWrapper(initialFormValues[fieldName])
                                                .map((item, index) => {
                                                    return (
                                                        <CustomFieldRow
                                                            key={item.id}
                                                            row={item}
                                                            values={values}
                                                            index={index}
                                                            field={field}
                                                        />
                                                    );
                                                })
                                                .valueOf()}
                                        </tbody>
                                    </Table>
                                </StyledContainer>
                            </fieldset>
                        </div>
                    );

                case FIELD_TYPES.LEGEND:
                default:
                    return (
                        <label key={fieldName} className="ticket-form__label padding-top-1-rem">
                            {field.label}
                        </label>
                    );
            }
        });
    };
    const CustomFieldRow = ({ row , values, index, field }) => {
        const { name, type, id } = row;
        return (
            <tr key={id}>
                <td>
                    <input
                        className="ticket-form__checkbox"
                        checked={values[field.type][index].selected}
                        type={FIELD_TYPES.CHECKBOX_INPUT}
                        onClick={(e) => handleClick(e, index, false)}
                        id={id}
                    />
                    {name}
                </td>
                <td>{type}</td>
                <td className="text-right">
                    <label className="switch">
                        <input
                            className="ticket-form__checkbox"
                            disabled={!values[field.type][index].selected}
                            checked={values[field.type][index].required}
                            onClick={(e) => handleClick(e, index, true)}
                            type={FIELD_TYPES.CHECKBOX_INPUT}
                            id={id}
                        />
                        <span className="slider round"></span>
                    </label>
                </td>
            </tr>
        );
    };

    const memoizedConstructedFormFields = useMemo(() => constructFormFields(formFields), [values, values[FIELD_TYPES.CUSTOM_FIELD] , formFields, inputValidationErrors, showSpanishTextField]);

    return (
        <form
            className="ticket-form"
            id="ticketForm"
            onSubmit={handleSubmit}
        >
            {
                alertMsg &&
                    <AlertMessage
                        key={alertMsg}
                        content={<FormattedMessage id={alertMsg} values={{ entity }}/>}
                        type="error"
                    />
            }

            {memoizedConstructedFormFields}

            <div className="ticket-form__controls">
                <Button
                    id='addNewTicketCategory'
                    className='usa-button  gi-button gi-button__secondary'
                    onClick={handleCancel}
                >
                    <FormattedMessage id="button.cancel"/>
                </Button>
                <Button
                    id='addNewTicketCategory'
                    className='usa-button  gi-button gi-button__primary'
                    onClick={handleSubmit}
                >
                    <FormattedMessage id="button.save"/>
                </Button>
            </div>

        </form>
    );
};


export default TicketForm;